import { Component, ViewChild } from '@angular/core';
import { TableSearchComponent } from '../../shared/table-search/table-search.component';
import { TableComponent } from '../../shared/table/table.component';
import { InstrumentRun, InstrumentRunTableData } from '../../interfaces/instrument-run';
import { RowData } from '../../shared/interfaces/table.data';
import { MatTableDataSource } from '@angular/material/table';
import { ButtonsComponent } from '../../shared/buttons/buttons.component';
import { MatDialog } from '@angular/material/dialog';
import { InstrumentRunService } from '../../services/instrument-run/instrument-run.service';
import { SampleDetailsModalComponent } from './sample-details-modal/sample-details-modal.component';
import * as InstrumentRunConst from '../../shared/constants/instrument-run';
import { ModalDialogComponent } from '../../shared/modal-dialog/modal-dialog.component';
import { MessageModalService } from '../../services/message-modal/message-modal.service';
import { DragDropModule } from '@angular/cdk/drag-drop';
import { MultiFileUploadModalComponent } from '../../shared/multi-file-upload-modal/multi-file-upload-modal.component';
import { environment } from '../../../environments/environment';
import { lastValueFrom, Subscription } from 'rxjs';
import { UploadService, UploadCompletionEvent } from '../../services/upload/upload.service';
import { UploadType } from '../../services/upload/upload-type';
import { MultiUploadModalData } from '../../shared/interfaces/modal.data';
import { SharePopupComponent } from '../../shared/share-popup/share-popup.component';
import { InstrumentRunEditModalComponent } from './edit-modal/instrument-run-edit-modal.component';
import { InstrumentRunInfoPanelComponent } from './info-panel/instrument-run-info-panel.component';

@Component({
  selector: 'app-instrument-run',
  standalone: true,
  imports: [TableSearchComponent, TableComponent, ButtonsComponent, DragDropModule, InstrumentRunInfoPanelComponent],
  templateUrl: './instrument-run.component.html',
  styleUrl: './instrument-run.component.css',
})
export class InstrumentRunComponent {
  dataSource: MatTableDataSource<RowData<InstrumentRunTableData>> = new MatTableDataSource<
    RowData<InstrumentRunTableData>
  >([]);
  btnStyleFilledSmallForPrimaryBtn = 'btn-filled-small';
  btnStyleFilledSmallForPrimaryBtnDisabled = 'btn-filled-small-disabled';
  btnMatFlat: string = 'mat-flat-button';
  btnName: string = $localize`Upload New Data`;
  btnNameShare: string = $localize`Share`;
  btnMatStroked = 'mat-stroked-button';
  btnStyleStroked = 'btn-stroked';
  displayedColumnLabels: string[] = [
    // InstrumentRunConst.runId,
    InstrumentRunConst.runName,
    InstrumentRunConst.groupName,
    InstrumentRunConst.user,
    InstrumentRunConst.size,
    InstrumentRunConst.uploadedOn,
    InstrumentRunConst.status,
  ];
  displayedColumns = InstrumentRunConst.displayedColumns;

  showCheckboxInTable: boolean = true;
  showActionBtnsInPagination: boolean = true;
  selectedInstrumentRunItems: InstrumentRunTableData[] = [];
  @ViewChild(TableComponent)
  tableComponent!: TableComponent<InstrumentRunTableData>;
  folderName: string = '';
  isInfoPanelOpen = false;
  selectedInstrumentRun: InstrumentRunTableData | null = null;

  /** Row with an open menu/panel/dialog; drives the "active" row highlight. */
  activeRowRunId: number | null = null;

  readonly isRowActiveFn = (row: RowData<InstrumentRunTableData>): boolean =>
    this.activeRowRunId != null && row.data.runID === this.activeRowRunId;

  uploadUrl = `${environment.apiBaseUrl}/api/v1/buckets/upload/instrument-run`;
  private uploadCompletionSubscription!: Subscription;

  constructor(
    private dialog: MatDialog,
    private instrumentRunService: InstrumentRunService,
    private messageModal: MessageModalService,
    private uploadService: UploadService
  ) {}

  ngOnInit() {
    this.loadData();
    this.uploadCompletionSubscription = this.uploadService.uploadCompleted$.subscribe(
      (event: UploadCompletionEvent) => {
        if (event.uploadType === UploadType.InstrumentRun) {
          this.updateTableRowStatus(event.resourceId, event.finalStatus, event.fileSize);
        }
      }
    );
  }

  private updateTableRowStatus(runId: string, status: 'completed' | 'error' | 'cancelled', fileSize: number): void {
    const runIdNumber = parseInt(runId, 10);
    if (isNaN(runIdNumber)) return;

    const rowIndex = this.dataSource.data.findIndex((row) => row.data.runID === runIdNumber);

    if (rowIndex > -1) {
      const updatedData = [...this.dataSource.data]; //copy of original data

      let newStatusString: InstrumentRunTableData['status'];
      if (status === 'completed') {
        newStatusString = 'uploaded'; //backend values
      } else if (status === 'error') {
        newStatusString = 'error';
      } else if (status === 'cancelled') {
        newStatusString = 'canceled';
      } else {
        return;
      }

      updatedData[rowIndex].data.status = newStatusString;

      if (status === 'completed' && fileSize !== undefined) {
        updatedData[rowIndex].data.size = fileSize;
      }
      this.dataSource.data = updatedData;
    }
  }

  loadData() {
    this.instrumentRunService.getInstrumentRunList().subscribe({
      next: (response) => {
        if (response.success) {
          const instrumentRunListData = Array.isArray(response.data) && response.data.length > 0 ? response.data : [];
          this.setInstrumentRunListData(instrumentRunListData);
        } else {
          this.messageModal.openModal($localize`Error`, $localize`${response.message}`);
        }
      },
      error: (err) => {
        console.error('Instrument run failed:', err);
        this.messageModal.openModal($localize`Error`, $localize`Error while fetching list of instrument run.`);
      },
    });
  }

  setInstrumentRunListData(instrumentRunListData: InstrumentRun[]) {
    const rowData: RowData<InstrumentRunTableData>[] = instrumentRunListData.map(
      (instrumentRunItem: InstrumentRun) => ({
        data: {
          status: instrumentRunItem.Status,
          runID: instrumentRunItem.InstrumentRunID,
          runName: instrumentRunItem.Name,
          user: instrumentRunItem.UserName,
          groupName: {
            list: instrumentRunItem.Groups.sort((a, b) => (a.IsPrimary === b.IsPrimary ? 0 : a.IsPrimary ? -1 : 1)).map(
              (g) => g.GroupName
            ),
          },
          uploadedOn: instrumentRunItem.UploadedOn,
          folderName: instrumentRunItem.FolderName,
          size: instrumentRunItem.Size,
          description: instrumentRunItem.Description,
          rawGroups: instrumentRunItem.Groups,
        },
        selected: false,
      })
    );
    this.dataSource = new MatTableDataSource<RowData<InstrumentRunTableData>>(rowData);
    this.dataSource.filterPredicate = this.createFilterPredicate();
  }

  public createFilterPredicate(): (item: RowData<InstrumentRunTableData>, filter: string) => boolean {
    return (item: RowData<InstrumentRunTableData>, filter: string) => {
      const terms = filter.trim().toLowerCase().split(/\s+/); // split by space
      const data = item.data;

      // Create a single string of all searchable data
      const searchableString = [
        data.runID,
        data.runName,
        data.user,
        data.status,
        data.uploadedOn.split('T')[0], // Only search date part
        ...(data.groupName.list || []), // Add group names to the searchable content
      ]
        .filter((val) => val != null)
        .join(' ')
        .toLowerCase();
      return terms.every((term) => searchableString.includes(term));
    };
  }

  onFilterChanged(filterValue: string): void {
    this.dataSource.filter = filterValue;
    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  openFileUploadModal(): void {
    this.selectedInstrumentRunItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    const dialogRef = this.dialog.open<MultiFileUploadModalComponent, MultiUploadModalData>(
      MultiFileUploadModalComponent,
      {
        width: '50rem',
        disableClose: true,
        data: {
          title: $localize`File Upload`,
          description: $localize`Select and upload the files of your choice`,
          primaryButtonLabel: $localize`Done`,
          secondaryButtonLabel: $localize`Cancel`,
          showPrimaryButton: true,
          showSecondaryButton: false,
          inputFileName: true,
          inputFileDescription: false,
          files: [],
          uploadConfig: {
            featureType: UploadType.InstrumentRun,
            acceptedFileTypes: '.zip',
            maxFileSize: 100000,
            useChunking: true,
            chunkSize: 10 * 1024 * 1024,
            apiUrl: this.uploadUrl,
            dictDefaultMessage: 'Drag instrument run files here or click to browse',
          },
        },
      }
    );
    this.tableComponent.resetSelections();
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.loadData();
      }
    });
  }

  handleCheckboxChange(rowData: { data: InstrumentRunTableData; selected: boolean }) {
    if (rowData.selected) {
      const exists = this.selectedInstrumentRunItems.some((row) => row.runID === rowData.data.runID);
      if (!exists) {
        this.selectedInstrumentRunItems.push(rowData.data);
      }
    } else {
      this.selectedInstrumentRunItems = this.selectedInstrumentRunItems.filter(
        (row) => row.runID !== rowData.data.runID
      );
    }
  }

  deleteSelectedItems() {
    const instrumentRunIds = this.selectedInstrumentRunItems.map((instrumentRunItem) => instrumentRunItem.runID);
    return this.instrumentRunService.deleteSelectedInstrumentRunItems(instrumentRunIds);
  }

  onCreateNewSampleBtnClicked(): void {
    const dialogRef = this.dialog.open(SampleDetailsModalComponent, {
      width: '50rem',
      data: {
        title: $localize`Enter Sample Details`,
        primaryButtonLabel: $localize`Done`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true,
        showSecondaryButton: true,
        namePlaceholder: $localize`Enter Sample Name`,
        descriptionPlaceholder: $localize`Enter Sample Description`,
      },
    });

    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe((formValues) => {
      this.handleCreateSampleDetails(formValues, modalInstance);
    });
  }

  openDeleteConfirmationModal() {
    const formatedInstrumenNames = this.getInstrumentNamesInCommaSeparated();
    const dialogRef = this.dialog.open(ModalDialogComponent, {
      width: '50rem',
      data: {
        title: $localize`Delete Instrument Run?`,
        description: $localize`Once deleted, <strong>${formatedInstrumenNames}</strong> will no longer be available in the instrument run list.`,
        primaryButtonLabel: $localize`Yes, Delete`,
        secondaryButtonLabel: $localize`Cancel`,
        showPrimaryButton: true, // Show primary button
        showSecondaryButton: true, // Secondary button is always shown
      },
    });
    const modalInstance = dialogRef.componentInstance;
    modalInstance.primaryAction.subscribe(() => {
      this.handleDelete(modalInstance);
    });
  }

  getInstrumentNamesInCommaSeparated(): string {
    return this.selectedInstrumentRunItems
      .map((item) => item.runName)
      .filter((runName) => runName)
      .join(', ');
  }

  getInstrumentRunIds(): number[] {
    return this.selectedInstrumentRunItems
      .map((instrumentRunItem) => instrumentRunItem.runID)
      .filter((runID) => typeof runID === 'number' && !isNaN(runID)); // Ensure runID is a valid number
  }

  handleDelete(modalInstance: ModalDialogComponent): void {
    this.deleteSelectedItems().subscribe({
      next: (response) => {
        if (response.success) {
          this.tableComponent.resetSelections();
          this.selectedInstrumentRunItems = [];
          this.loadData();
          modalInstance.closeModal(true); // Close the modal on success
        } else {
          modalInstance.showError(response.message); // Show error inside modal
        }
      },
      error: (err) => {
        modalInstance.showError($localize`Error while deleting the instrument run.`);
        console.error('Instrument run failed:', err);
      },
    });
  }

  handleCreateSampleDetails(
    formValues: { sampleName: string; sampleDescription: string },
    modalInstance: SampleDetailsModalComponent
  ) {
    this.createSampleDetails(formValues).subscribe({
      next: (response) => {
        if (response.success) {
          this.tableComponent.resetSelections();
          this.loadData();
          this.selectedInstrumentRunItems = [];
          modalInstance.closeModal(true);
        } else {
          modalInstance.showError($localize`${response.message}`);
        }
      },
      error: (err) => {
        modalInstance.showError($localize`Error while creating the sample.`);
        console.error('Instrument run failed:', err);
      },
    });
  }

  createSampleDetails(formValues: { sampleName: string; sampleDescription: string }) {
    const { sampleName = '', sampleDescription = '' } = formValues;
    const sampleDetails = { sampleName, sampleDescription };
    const instrumentRunIds = this.getInstrumentRunIds();
    return this.instrumentRunService.createSampleItemForSelectedInstrumentIds(sampleDetails, instrumentRunIds);
  }

  acceptedFileTypes = '.zip';
  maxFileSize = 100000; // 100GB
  useChunking = true;
  chunkSize = 10 * 1024 * 1024; // 10MB chunks

  ngOnDestroy(): void {
    if (this.uploadCompletionSubscription) {
      this.uploadCompletionSubscription.unsubscribe();
    }
  }

  onShareClicked() {
    const dialogRef = this.dialog.open(SharePopupComponent, {
      width: '550px',
      data: {
        Ids: this.selectedInstrumentRunItems.map((item) => item.runID),
        title: 'Instrument Run',
        resource_path: 'instrument-runs',
      },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.selectedInstrumentRunItems = [];
        if (this.tableComponent) {
          this.tableComponent.resetSelections();
        }
        this.loadData();
      }
    });
  }
  onGetInfoClicked(data: InstrumentRunTableData) {
    this.selectedInstrumentRun = data;
    this.isInfoPanelOpen = true;
    this.activeRowRunId = data.runID;
  }

  onInfoPanelClosed(): void {
    this.isInfoPanelOpen = false;
    this.activeRowRunId = null;
  }

  onRowMenuOpened(row: InstrumentRunTableData): void {
    this.activeRowRunId = row.runID;
  }

  onRowMenuClosed(): void {
    // Keep the highlight if a menu item opened the info panel or a dialog — that surface now owns it.
    if (!this.isInfoPanelOpen && this.dialog.openDialogs.length === 0) {
      this.activeRowRunId = null;
    }
  }

  async onEditClicked(data: InstrumentRunTableData) {
    this.selectedInstrumentRunItems = [];
    if (this.tableComponent) {
      this.tableComponent.resetSelections();
    }
    this.activeRowRunId = data.runID;
    const dialogRef = this.dialog.open(InstrumentRunEditModalComponent, {
      width: '50rem',
      data: {
        runData: data,
      },
    });

    const res = await lastValueFrom(dialogRef.afterClosed());
    this.activeRowRunId = null;

    if (res && res.event === 'RELOAD') {
      this.messageModal.openModal($localize`Success`, $localize`Instrument Run updated successfully.`);
      this.tableComponent.resetSelections();
      this.loadData();
    }
  }
}
